/**
 * The purpose of this strict typing is to prevent invalid states and invalid transitions between states on a type level
 */
import { DisplayAssetsFormData } from '../../types';

interface BaseApi<Name extends string, State extends unknown, Actions extends unknown> {
  state: State & {
    type: Name;
  };
  remainingSeconds: number | null;
  actions: Actions;
}

export type Inactive = BaseApi<'inactive', unknown, unknown>;

export type ApprovedWelcome = BaseApi<'active.approved.welcome', unknown, { confirm: () => void; cancel: () => void }>;

export type AskingForRenewal = BaseApi<'active.askingForRenewal', unknown, { accept: () => void; cancel: () => void }>;

export type ApprovedNoAssets = BaseApi<'active.approved.noAssets', unknown, { askForDisplayingAssets: () => void }>;

export type ApprovedAskingForViewAssets = BaseApi<
  'active.approved.askingForViewAssets',
  { formData: DisplayAssetsFormData },
  { updateForm: (formData: DisplayAssetsFormData) => void; cancel: () => void; submit: () => void }
>;

export type ApprovedViewingAssets = BaseApi<'active.approved.viewingAssets', unknown, { turnDisplayingAssetsOf: () => void }>;

/**
 * The way to narrow types down for nested discriminated unions (union of objects that contain state.type deep prop)
 * https://github.com/microsoft/TypeScript/issues/18758#issuecomment-1172487806
 */

export type LoginAsFlow = Inactive | ApprovedWelcome | AskingForRenewal | ApprovedNoAssets | ApprovedAskingForViewAssets | ApprovedViewingAssets;

type GetApiForState<StateType, Api> = Api extends { state: { type: StateType } } ? Api : never;

export const isFlowInState = <StateType extends LoginAsFlow['state']['type']>(
  flow: LoginAsFlow,
  name: StateType
): flow is GetApiForState<StateType, LoginAsFlow> => flow.state.type === name;
